import styled from "@emotion/styled";

import Button from "components/ui/button/Button";
import {
  SidebarFooter,
  SidebarMenu,
  SidebarWrapper,
  useSidebar,
} from "components/ui/sidebar/Sidebar";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { routes } from "lib/routes";
import { performLogout } from "lib/userUtils";
import { NavItemButton, NavItemLink } from "./utils";

type FooterProps = {
  isGuestUser: boolean;
};

const Footer = ({ isGuestUser }: FooterProps) => {
  const { toggleSidebar, isMobile } = useSidebar();

  // Mobile: Help and Logout as labelled items at either end. Logout lives here because the account
  // dropdown that holds it on desktop (Header) is hidden on mobile. No sidebar toggle: no side nav.
  if (isMobile) {
    return (
      <MobileSidebarFooter>
        <MobileFooterMenu>
          <NavItemLink label="Help" href={routes.help} icon="helpCircle" />
          {/* Anonymous viewers of public report/lead URLs have no account to log out of. */}
          {!isGuestUser && (
            <NavItemButton label="Logout" icon="logOut01" onClick={() => performLogout()} />
          )}
        </MobileFooterMenu>
      </MobileSidebarFooter>
    );
  }

  return (
    <SidebarFooterStyled>
      <SidebarMenuFooter>
        <NavItemLink label="Help" href={routes.help} icon="helpCircle" iconOnly />
      </SidebarMenuFooter>
      <TooltipComponent
        content="Toggle sidebar"
        keyboardShortcut={{ ctrlKey: true, keys: ["\\"], macOsRemapKeys: true }}
      >
        <SidebarMenuToggle
          buttonType="secondary"
          icon="layoutLeft"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        />
      </TooltipComponent>
    </SidebarFooterStyled>
  );
};

const SidebarFooterStyled = styled(SidebarFooter)(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
  flexDirection: "row",
  justifyContent: "space-between",
  gap: 0,
}));

// Mobile footer: 16px inset, with extra bottom padding to clear the home indicator on notched
// phones. Separate from the desktop footer, which keeps SidebarFooter's 8px padding + toggle row.
const MobileSidebarFooter = styled(SidebarFooter)(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
  padding: "16px",
  paddingBottom: "max(16px, env(safe-area-inset-bottom))",
  gap: 0,
}));

// Help at one end, Logout at the other. Overrides NavItemInternalList's sidebar-width calc,
// which assumes one full-width item per row.
const MobileFooterMenu = styled(SidebarMenu)({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 0,
  // Footer rows hug their content (Help left, Logout right) instead of filling the row. The
  // data-mobile-nav prefix outweighs the full-width rule utils.tsx applies to nav rows in the panel.
  '[data-mobile-nav="true"] & > li:not([data-icon-only=true])': {
    width: "auto",
  },
  "& > li": {
    width: "auto",
  },
});

const SidebarMenuFooter = styled(SidebarMenu)({
  flexDirection: "row",
  gap: 0,
  transition: "opacity 80ms ease-in-out, visibility 80ms ease-in-out",
  transitionDelay: "120ms",
  [`${SidebarWrapper}[data-collapsible=icon] &`]: {
    opacity: 0,
    visibility: "hidden",
    transitionDelay: "0ms",
  },
});

const SidebarMenuToggle = styled(Button)({
  transition: "background-color 0.2s, border-color 0.2s, margin 0.2s", // both colours are the default button transition
  [`${SidebarWrapper}[data-collapsible=icon] &`]: {
    margin: "0 8px",
  },
});

export default Footer;
